/**
 * Helpers do move otimista de status, compartilhados por Kanban e Lista.
 *
 * As duas views movem a tarefa localmente no drop e só depois persistem, então
 * ambas precisam do mesmo par: fotografar o status atual (para desfazer) e
 * resolver a categoria do status de destino (para os buckets de visão rápida,
 * que leem `statusCategory` embutido em vez do slice da pasta).
 */

/**
 * Id do status atual de uma tarefa, nas duas grafias que convivem no módulo:
 * `statusId` (payload serializado) e `id_status` (coluna crua).
 *
 * NÃO cobre a terceira grafia, `task.status` literal pré-migração de status
 * customizáveis — quem precisa dela é o agrupamento por bucket da Lista, que
 * mantém a própria resolução (`taskBucketKey`, em `task-list-view.vue`).
 * Trocar uma pela outra perde o fallback legado em silêncio.
 *
 * @param {object} task
 * @returns {string|number|null}
 */
export function taskStatusId(task) {
  if (!task) return null
  return task.statusId == null ? task.id_status : task.statusId
}

/**
 * Fotografa os campos que `PATCH_TASK_STATUS` sobrescreve, para que um move
 * que falhou possa ser desfeito exatamente.
 *
 * @param {object} task
 * @returns {{ statusId: (string|number|null), statusCategory: (string|undefined) }}
 *
 * @example
 * const previous = snapshotTaskStatus(task)
 * // ... move falhou
 * store.dispatch('tarefas/patchTaskStatus', { id: task.id, ...previous })
 */
export function snapshotTaskStatus(task) {
  if (!task) return { statusId: null, statusCategory: undefined }
  const statusId = taskStatusId(task)
  return {
    statusId: statusId == null ? null : statusId,
    statusCategory: task.statusCategory,
  }
}

/**
 * Categoria do status de destino, resolvida pelo getter `statusCategoryById`.
 *
 * Tolerante a um store sem esse getter (montagens parciais em teste, consumidor
 * que registra só uma fatia do módulo): devolve `null` em vez de estourar. Um
 * `statusCategory` nulo não quebra o bucket por status — ele é resolvido por
 * `statusId` contra o slice da pasta; a categoria só serve de fallback na visão
 * rápida cross-pasta, onde o drag está desabilitado.
 *
 * @param {object} storeGetters - `this.$store.getters`
 * @param {string|number} statusId
 * @returns {string|null}
 */
export function resolveStatusCategory(storeGetters, statusId) {
  const resolver = storeGetters && storeGetters['tarefas/statusCategoryById']
  if (typeof resolver !== 'function') return null
  return resolver(statusId) || null
}
